import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { selectToken } from 'containers/authentication/authenticationSlice'
import OrganizationsContext from 'containers/organizations/organizationsContext'
import useGetOrganizations from 'containers/organizations/useGetOrganizations'
import Modal from 'components/Modal'
import ScreenLoading from 'components/ScreenLoading'
import api from 'containers/api'
import { IOrganization, IServiceAccount } from 'types'
import { SERVICE_ACCOUNT_CONNECTED } from 'utils/serviceAccountEvents'
import './CurrencyMismatchPrompt.scss'

// "Your Shopify account reports in USD; your reporting currency is EUR. Switch?" — shown once per
// account currency (the dismissal is stored on the organization), whichever path connected the
// account: the OAuth account picker, the single-account verify, or the Shopify install callback.
// The server decides what is a mismatch (`currency_mismatch` on GET /service_accounts) and, on a
// switch, settles every other account currency, so the prompt cannot cycle between two accounts.
// It rechecks after a connect (window event), on navigation (a Shopify account only becomes
// active after the subscription webhook, with no browser event), after a dismissal and after a
// switch; a response from a previous organization is discarded, and so is a reload requested by a
// Switch/Keep handler that was started on a previous organization.
const CurrencyMismatchPrompt = ({ currentOrganization }: { currentOrganization: IOrganization }) => {
  const token = useSelector(selectToken)
  const { pathname } = useLocation()
  const { updateCurrentOrganization } = useContext(OrganizationsContext)
  const { clearOrganizationsCache, fetchOrganizations } = useGetOrganizations()
  const [account, setAccount] = useState<IServiceAccount | null>(null)
  const [pending, setPending] = useState(false)
  const [error, setError] = useState('')
  const requestSeq = useRef(0)
  const activeOrganizationId = useRef<number | undefined>(undefined)

  const organizationId = currentOrganization?.id
  activeOrganizationId.current = organizationId   // what the page shows now; a stale closure compares against it
  const isDemo = !!currentOrganization?.is_demo

  const loadMismatch = useCallback(async () => {
    if (!organizationId || isDemo) {
      requestSeq.current++          // a load still in flight for the previous organization is void
      setAccount(null)
      return
    }
    if (organizationId !== activeOrganizationId.current) return   // a Switch/Keep handler from a previous organization
    const seq = ++requestSeq.current
    try {
      const accounts: IServiceAccount[] = await api.getConnectedServiceAccounts({ token, organizationId })
      if (seq !== requestSeq.current) return   // a newer load superseded this one
      if (organizationId !== activeOrganizationId.current) return   // the organization changed while this load ran
      setAccount(accounts.find((a) => a.currency_mismatch && a.currency) || null)
    } catch (err) {
      // the prompt is a courtesy; a failed load must never block the page
    }
  }, [organizationId, isDemo, token])

  useEffect(() => {
    setAccount(null)
    loadMismatch()
  }, [loadMismatch, currentOrganization?.currency_id, pathname])

  useEffect(() => {
    const onConnected = () => { loadMismatch() }
    window.addEventListener(SERVICE_ACCOUNT_CONNECTED, onConnected)
    return () => window.removeEventListener(SERVICE_ACCOUNT_CONNECTED, onConnected)
  }, [loadMismatch])

  if (!account || !account.currency) return null

  const accountCurrency = account.currency
  const orgCurrency = currentOrganization.currency_id
  const serviceName = account.service.replace(/_/g, ' ')

  const applySavedOrganization = async (saved: Partial<IOrganization>) => {
    if (updateCurrentOrganization) updateCurrentOrganization(saved)
    await clearOrganizationsCache()
    await fetchOrganizations()
  }

  const handleSwitch = async () => {
    if (pending) return
    setPending(true)
    setError('')
    try {
      const saved = await api.updateOrganization({ organizationId: currentOrganization.id, token, currency: accountCurrency })
      await applySavedOrganization(saved)
      await loadMismatch()   // the server settled the other currencies; normally nothing is left
    } catch (err) {
      setError('Could not change the reporting currency. You can retry from Settings → Organization.')
    } finally {
      setPending(false)
    }
  }

  const handleKeep = async () => {
    if (pending) return                 // closing the modal mid-Switch must not also dismiss the currency
    setPending(true)
    setError('')
    try {
      const saved = await api.dismissCurrencyPrompt({ organizationId: currentOrganization.id, token, currency: accountCurrency })
      await applySavedOrganization(saved)
      await loadMismatch()   // the next mismatched currency, if any
    } catch (err) {
      setAccount(null)   // dismissing is best-effort: the prompt simply returns next time
    } finally {
      setPending(false)
    }
  }

  const serviceLabel = serviceName.replace(/\b\w/g, (c) => c.toUpperCase())

  return (
    <Modal show={true} onHide={pending ? () => undefined : handleKeep} className="currency-mismatch-modal">
      <div className="currency-mismatch">
        <h3>Reporting currency</h3>
        <p>
          Your <strong>{serviceLabel}</strong> account <em>{account.name}</em> reports in a different currency
          from your organization.
        </p>
        <div className="currency-compare">
          <div className="currency-compare__item">
            <span className="currency-compare__label">{serviceLabel} account</span>
            <span className="currency-compare__code">{accountCurrency}</span>
          </div>
          <span className="material-icons-outlined currency-compare__arrow" aria-hidden="true">arrow_forward</span>
          <div className="currency-compare__item currency-compare__item--current">
            <span className="currency-compare__label">Reporting currency</span>
            <span className="currency-compare__code">{orgCurrency}</span>
          </div>
        </div>
        {currentOrganization.is_admin ? (
          <>
            <p className="currency-mismatch__note">
              All amounts in Maya are shown in the reporting currency. Switching to {accountCurrency} recalculates
              all your figures, past ones included; dashboards update after the next data refresh.
            </p>
            {error && <p className="form-error">{error}</p>}
            <div className="currency-mismatch__actions">
              <button type="button" className="submit-button inverted" onClick={handleKeep} disabled={pending}>
                Keep {orgCurrency}
              </button>
              <button type="button" className="submit-button" onClick={handleSwitch} disabled={pending}>
                {pending ? <ScreenLoading spinnerColor="white" /> : `Switch to ${accountCurrency}`}
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="currency-mismatch__note">
              Every amount in Maya is shown in the reporting currency. An organization administrator can change it in
              Settings → Organization.
            </p>
            <div className="currency-mismatch__actions">
              <button type="button" className="submit-button" onClick={handleKeep} disabled={pending}>
                OK
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  )
}

export default CurrencyMismatchPrompt
